import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import { useApi } from '../hooks/useApi';

/**
 * DemoTenantsPage - read-only Uebersicht der DEMO-/Trial-Mandanten.
 *
 * Zwei Quellen (Backend GET /api/platform/admin/demo-tenants?product=):
 *  - live:     echte DEMO-Mandanten/User je Produkt, per Fan-out an die
 *              DEMO-Instanzen (demo.prism4iso.com / demo.prism4audit.com).
 *              Die Website-Trials entstehen dort lokal (nicht in der Registry).
 *  - registry: PlatformTenant (status trial/demo), vom Portal verwaltet.
 *
 * Produktfilter (alle | p4i | p4a) laedt mit ?product= neu. platform_admin-gated.
 */
function Th({ children }) {
  return <th style={{ textAlign: 'left', fontSize: 11, textTransform: 'uppercase',
    letterSpacing: '.5px', color: 'var(--text-muted)', padding: '6px 10px' }}>{children}</th>;
}
function Td({ children, mono }) {
  return <td style={{ fontSize: 13, padding: '6px 10px', color: 'var(--text)',
    fontFamily: mono ? 'monospace' : undefined }}>{children}</td>;
}

function dot(ok) {
  return <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
    background: ok ? '#10b981' : '#ef4444', marginRight: 6 }} />;
}

function LiveCard({ row, t }) {
  const ok = !!row.reachable;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px',
      minWidth: 200, background: 'var(--bg-surface)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px' }}>
        {dot(ok)}{row.tool} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>DEMO</span>
      </div>
      {ok ? (
        <>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 6 }}>
            {row.tenants_total ?? '-'}
            <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}> {t('demotenants.tenants', 'Mandanten')}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {row.users_total ?? '-'} {t('demotenants.users', 'Nutzer')}
          </div>
          {row.tenants_by_status && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              {Object.entries(row.tenants_by_status).map(([k, v]) => `${k}: ${v}`).join(' | ')}
            </div>
          )}
        </>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--danger, #ef4444)', marginTop: 6 }}>
          {t('demotenants.unreachable', 'nicht erreichbar')}{row.error ? ` (${row.error})` : ''}
        </div>
      )}
    </div>
  );
}

const PRODUCTS = [
  ['all', 'Alle'],
  ['p4i', 'P4I'],
  ['p4a', 'P4A'],
];

export default function DemoTenantsPage() {
  const { t } = useTranslation();
  const { api } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [product, setProduct] = useState('all');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      setData(await api(`/admin/demo-tenants?product=${encodeURIComponent(product)}`));
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [api, product]);

  useEffect(() => { load(); }, [load]);

  const live = data?.live || [];
  const tenants = data?.tenants || [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>
          {t('demotenants.title', 'DEMO-/Trial-Mandanten')}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {PRODUCTS.map(([key, label]) => (
            <button key={key} type="button"
              className={`btn btn-sm ${product === key ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setProduct(key)} disabled={loading}>
              {label}
            </button>
          ))}
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={loading}>
          {loading ? t('common.loading', 'Lade...') : t('instances.refresh', 'Aktualisieren')}
        </button>
      </div>

      <div style={{ background: 'rgba(37,64,95,.10)', border: '1px solid rgba(37,64,95,.35)',
        borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: 'var(--text)', marginBottom: 14, lineHeight: 1.6 }}>
        {t('demotenants.hint2',
          'Live-Zahlen je Produkt stammen direkt von den DEMO-Instanzen (echte Website-Trials). '
          + 'Die Tabelle darunter zeigt zusaetzlich die vom Portal verwaltete PlatformTenant-Registry (trial/demo).')}
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)',
          borderRadius: 8, padding: '8px 12px', fontSize: 12.5, color: 'var(--danger, #ef4444)', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Live-Zahlen je Produkt */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
        {live.map((row) => <LiveCard key={row.tool} row={row} t={t} />)}
        {live.length === 0 && !loading && (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {t('demotenants.nolive', 'Keine DEMO-Instanz fuer die Auswahl konfiguriert.')}
          </div>
        )}
      </div>

      {/* Registry (Zusatzinfo) */}
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '.5px', marginBottom: 6 }}>
        {t('demotenants.registry', 'PlatformTenant-Registry')} ({tenants.length})
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ background: 'var(--bg-surface)' }}>
            <tr>
              <Th>{t('demotenants.name', 'Mandant')}</Th>
              <Th>{t('demotenants.status', 'Status')}</Th>
              <Th>{t('demotenants.tier', 'Tier')}</Th>
              <Th>{t('demotenants.type', 'Typ')}</Th>
              <Th>{t('demotenants.contact', 'Kontakt')}</Th>
              <Th>{t('demotenants.valid_until', 'Gueltig bis')}</Th>
              <Th>{t('demotenants.created', 'Angelegt')}</Th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tn) => (
              <tr key={tn.id} style={{ borderTop: '1px solid var(--border)' }}>
                <Td>{tn.name}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tn.slug}</div></Td>
                <Td>{tn.status}</Td>
                <Td>{tn.tier}</Td>
                <Td>{tn.tenant_type}</Td>
                <Td>{tn.contact_email || '-'}</Td>
                <Td>{tn.valid_until || '-'}</Td>
                <Td>{tn.created_at ? new Date(tn.created_at).toLocaleDateString() : '-'}</Td>
              </tr>
            ))}
            {tenants.length === 0 && !loading && (
              <tr><Td>{t('demotenants.none', 'Keine DEMO-/Trial-Mandanten in der Registry.')}</Td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
